// src/pages/notifications/NotificationEdit.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Paper, 
  Grid, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Chip,
  FormHelperText,
  Snackbar,
  Alert,
  CircularProgress,
  Divider,
  FormControlLabel,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { Save as SaveIcon, Delete as DeleteIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { doc, getDoc, updateDoc, deleteDoc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { db } from '../../../firebase';

const NotificationEdit = () => {
  const { notificationId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const [formData, setFormData] = useState({
    título: '',
    descripción: '',
    tipo: 'general',
    fecha: new Date(),
    cursos: [],
    enlaces: [],
    adjuntos: [],
    visible: true
  });

  const [enlaceInput, setEnlaceInput] = useState({ nombre: '', url: '' });
  const [cursoInput, setCursoInput] = useState('');
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    const fetchNotification = async () => {
      try {
        const docRef = doc(db, 'notifications', notificationId);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          const data = docSnap.data();
          
          // Convert Firestore timestamp to Date
          const fecha = data.fecha instanceof Timestamp ? 
            data.fecha.toDate() : 
            new Date(data.fecha?.seconds * 1000 || Date.now());
          
          setFormData({
            ...data,
            fecha
          });
        } else {
          setError('No se encontró la notificación');
          navigate('/admin/notifications');
        }
      } catch (error) {
        console.error('Error fetching notification:', error);
        setError('Error al cargar la notificación');
      } finally {
        setFetching(false);
      }
    };

    fetchNotification();
  }, [notificationId, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear error when field is edited
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: ''
      });
    }
  };

  const handleSwitchChange = (e) => {
    const { name, checked } = e.target;
    setFormData({
      ...formData,
      [name]: checked
    });
  };

  const handleDateChange = (newDate) => {
    setFormData({
      ...formData,
      fecha: newDate
    });
  };

  const handleAddEnlace = () => {
    if (!enlaceInput.nombre || !enlaceInput.url) {
      setFormErrors({
        ...formErrors,
        enlaces: 'El nombre y la URL son obligatorios'
      });
      return;
    }

    setFormData({
      ...formData,
      enlaces: [...(formData.enlaces || []), { ...enlaceInput }]
    });
    setEnlaceInput({ nombre: '', url: '' });
    
    // Clear error
    if (formErrors.enlaces) {
      setFormErrors({
        ...formErrors,
        enlaces: ''
      });
    }
  };

  const handleRemoveEnlace = (index) => {
    const newEnlaces = [...(formData.enlaces || [])];
    newEnlaces.splice(index, 1);
    setFormData({
      ...formData,
      enlaces: newEnlaces
    });
  };

  const handleAddCurso = () => {
    if (!cursoInput) {
      setFormErrors({
        ...formErrors,
        cursos: 'El curso no puede estar vacío'
      });
      return;
    }

    if (!(formData.cursos || []).includes(cursoInput)) {
      setFormData({
        ...formData,
        cursos: [...(formData.cursos || []), cursoInput]
      });
    }
    setCursoInput('');
    
    // Clear error
    if (formErrors.cursos) {
      setFormErrors({
        ...formErrors,
        cursos: ''
      });
    }
  };

  const handleRemoveCurso = (curso) => {
    setFormData({
      ...formData,
      cursos: (formData.cursos || []).filter(c => c !== curso)
    });
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.título) {
      errors.título = 'El título es obligatorio';
    }
    
    if (!formData.descripción) {
      errors.descripción = 'La descripción es obligatoria';
    }
    
    if (!formData.tipo) {
      errors.tipo = 'El tipo es obligatorio';
    }
    
    if (formData.tipo === 'curso' && (!formData.cursos || formData.cursos.length === 0)) {
      errors.cursos = 'Debe seleccionar al menos un curso';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      // Format data for Firestore
      const notificationData = {
        ...formData,
        updatedAt: serverTimestamp()
      };
      
      await updateDoc(doc(db, 'notifications', notificationId), notificationData);
      setShowSuccess(true);
    } catch (error) {
      console.error('Error updating notification:', error);
      setError('Error al actualizar la notificación. Inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  // Continuación de src/pages/notifications/NotificationEdit.js

  const handleDelete = async () => {
    try {
      await deleteDoc(doc(db, 'notifications', notificationId));
      navigate('/admin/notifications');
    } catch (error) {
      console.error('Error deleting notification:', error);
      setError('Error al eliminar la notificación');
    }
  };

  if (fetching) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5">Editar Notificación</Typography>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/admin/notifications')}
          >
            Volver
          </Button>
        </Box>
        
        <Divider sx={{ mb: 3 }} />
        
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Título"
                name="título"
                value={formData.título || ''}
                onChange={handleChange}
                error={!!formErrors.título}
                helperText={formErrors.título}
                required
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Descripción"
                name="descripción"
                value={formData.descripción || ''}
                onChange={handleChange}
                multiline
                rows={4}
                error={!!formErrors.descripción}
                helperText={formErrors.descripción}
                required
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!formErrors.tipo}>
                <InputLabel>Tipo</InputLabel>
                <Select
                  name="tipo"
                  value={formData.tipo || 'general'}
                  onChange={handleChange}
                  label="Tipo"
                >
                  <MenuItem value="general">General</MenuItem>
                  <MenuItem value="urgent">Urgente</MenuItem>
                  <MenuItem value="academic">Académico</MenuItem>
                  <MenuItem value="activities">Actividades</MenuItem>
                  <MenuItem value="shop">Tienda</MenuItem>
                  <MenuItem value="curso">Específico del curso</MenuItem>
                </Select>
                {formErrors.tipo && <FormHelperText>{formErrors.tipo}</FormHelperText>}
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <DateTimePicker
                label="Fecha"
                value={formData.fecha}
                onChange={handleDateChange}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </Grid>
            
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.visible || false}
                    onChange={handleSwitchChange}
                    name="visible"
                  />
                }
                label="Visible para los usuarios"
              />
            </Grid>
            
            {formData.tipo === 'curso' && (
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  Cursos
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <TextField
                    label="Añadir curso"
                    value={cursoInput}
                    onChange={(e) => setCursoInput(e.target.value)}
                    error={!!formErrors.cursos}
                    helperText={formErrors.cursos}
                    sx={{ mr: 1, flexGrow: 1 }}
                  />
                  <Button variant="outlined" onClick={handleAddCurso}>
                    Añadir
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {(formData.cursos || []).map((curso, index) => (
                    <Chip
                      key={index}
                      label={curso}
                      onDelete={() => handleRemoveCurso(curso)}
                    />
                  ))}
                </Box>
              </Grid>
            )}
            
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Enlaces
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={5}>
                    <TextField
                      fullWidth
                      label="Nombre del enlace"
                      value={enlaceInput.nombre}
                      onChange={(e) => setEnlaceInput({ ...enlaceInput, nombre: e.target.value })}
                      error={!!formErrors.enlaces}
                    />
                  </Grid>
                  <Grid item xs={12} sm={5}>
                    <TextField
                      fullWidth
                      label="URL"
                      value={enlaceInput.url}
                      onChange={(e) => setEnlaceInput({ ...enlaceInput, url: e.target.value })}
                      error={!!formErrors.enlaces}
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <Button
                      variant="outlined"
                      onClick={handleAddEnlace}
                      fullWidth
                      sx={{ height: '100%' }}
                    >
                      Añadir
                    </Button>
                  </Grid>
                </Grid>
                {formErrors.enlaces && (
                  <FormHelperText error>{formErrors.enlaces}</FormHelperText>
                )}
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {(formData.enlaces || []).map((enlace, index) => (
                  <Chip
                    key={index}
                    label={`${enlace.nombre}: ${enlace.url}`}
                    onDelete={() => handleRemoveEnlace(index)}
                  />
                ))}
              </Box>
            </Grid>
            
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mt: 2 }}>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => setDeleteDialogOpen(true)}
                  disabled={loading}
                >
                  Eliminar
                </Button>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/admin/notifications')}
                    disabled={loading}
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                    disabled={loading}
                  >
                    Guardar
                  </Button>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
      
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirmar eliminación</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Estás seguro de que deseas eliminar esta notificación? Esta acción no se puede deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleDelete} color="error" variant="contained">Eliminar</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={showSuccess}
        autoHideDuration={4000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success">
          Notificación actualizada exitosamente
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!error}
        autoHideDuration={4000}
        onClose={() => setError('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error">
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default NotificationEdit;